<template>
  <PayrollShell>
    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>
      <MonthlyConfigurationFilters
        mode="payroll"
        :show-search-results-table="false"
        :api-base="apiBase"
        :hide-date-range="hideDateRange"
      />

    <div class="px-2 pb-6 pt-2">
      <template v-if="showPayrollTable">
        <div class="w-full">
          <p class="text-[0.78rem] font-semibold text-[#1e293b] mb-3 m-0">
            {{ payrollPeriodTitle }}
          </p>

          <div class="payroll-table-scroll relative overflow-auto min-h-[220px] max-h-[calc(100vh-260px)]">
          <table class="payroll-table w-full min-w-[1200px] border-collapse">
            <thead>
              <tr>
                <th class="payroll-col-no">No.</th>
                <th class="payroll-col-employee payroll-sticky-col payroll-sticky-employee">Employee</th>
                <th class="num">Basic Salary</th>
                <th class="num">Working Days</th>
                <th class="num">Monthly Salary</th>
                <th class="num col-overt">Over Time</th>
                <th class="num">Total OT</th>
                <th class="num col-flyout">Earn</th>
                <th class="num">Total Earn</th>
                <th class="num col-flyout">Deduct</th>
                <th class="num">Total Deduct</th>
                <th class="num">Gross Salary</th>
                <th class="num">Income Tax</th>
                <th class="num">{{ pensionEmployeeHeader }}</th>
                <th class="num">{{ pensionCompanyHeader }}</th>
                <th class="num">Other Deduct</th>
                <th class="num">Net Pay</th>
                <th class="num payroll-col-status">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in sortedPayrollRows"
                :key="row.userId || index"
                class="hover:bg-slate-50 transition-colors border-b"
              >
                <td class="payroll-col-no">{{ index + 1 }}</td>
                <td class="payroll-col-employee payroll-sticky-col payroll-sticky-employee">{{ row.employee }}</td>
                <td class="num">{{ fmt(row.basicSalary) }}</td>
                <td class="num">
                  <input
                    v-model.number="row.workingDays"
                    type="number"
                    step="0.01"
                    min="0"
                    class="income-table-field"
                    :disabled="!canPreparePayroll || isSavingPayroll"
                    :readonly="!canPreparePayroll || isSavingPayroll"
                    :aria-label="`Working days for ${row.employee}`"
                    @change="onWorkingDaysChange(row)"
                  >
                </td>
                <td class="num">{{ fmt(row.monthlySalary) }}</td>
                <td class="num col-overt">
                  <button
                    type="button"
                    class="flyout-action-btn"
                    @click.stop="openFlyout($event, row.userId, 'ot')"
                  >
                    {{ flyoutButtonLabel(row.overtimeEntries) }} ▾
                  </button>
                </td>
                <td class="num">
                  <input
                    v-model.number="row.totalOt"
                    type="number"
                    step="0.01"
                    min="0"
                    class="income-table-field"
                    :disabled="!canPreparePayroll || isSavingPayroll"
                    :readonly="!canPreparePayroll || isSavingPayroll"
                    :aria-label="`Total OT for ${row.employee}`"
                    @change="onTotalOtChange(row)"
                  >
                </td>
                <td class="num col-flyout">
                  <button
                    type="button"
                    class="flyout-action-btn"
                    @click.stop="openFlyout($event, row.userId, 'earn')"
                  >
                    {{ flyoutButtonLabel(row.earnEntries) }} ▾
                  </button>
                </td>
                <td class="num">
                  <input
                    v-model.number="row.totalEarn"
                    type="number"
                    step="0.01"
                    min="0"
                    class="income-table-field"
                    :disabled="!canPreparePayroll || isSavingPayroll"
                    :readonly="!canPreparePayroll || isSavingPayroll"
                    :aria-label="`Total Earn for ${row.employee}`"
                    @change="onTotalEarnChange(row)"
                  >
                </td>
                <td class="num col-flyout">
                  <button
                    type="button"
                    class="flyout-action-btn"
                    @click.stop="openFlyout($event, row.userId, 'deduct')"
                  >
                    {{ flyoutButtonLabel(row.deductEntries) }} ▾
                  </button>
                </td>
                <td class="num">
                  <input
                    v-model.number="row.totalConfigDeduct"
                    type="number"
                    step="0.01"
                    min="0"
                    class="income-table-field"
                    :disabled="!canPreparePayroll || isSavingPayroll"
                    :readonly="!canPreparePayroll || isSavingPayroll"
                    :aria-label="`Total Deduct for ${row.employee}`"
                    @change="onTotalConfigDeductChange(row)"
                  >
                </td>
                <td class="num payroll-highlight">{{ fmt(row.grossSalary) }}</td>
                <td class="num">{{ fmt(row.incomeTax) }}</td>
                <td class="num">{{ fmt(row.pen7) }}</td>
                <td class="num">{{ fmt(row.pen11) }}</td>
                <td class="num">{{ fmt(row.otherDeduct) }}</td>
                <td class="num payroll-highlight">{{ fmt(row.netPay) }}</td>
                <td class="num payroll-col-status">
                  <input
                    type="checkbox"
                    class="payroll-status-checkbox"
                    :checked="!!row.status"
                    :disabled="!canPreparePayroll || isSavingPayroll"
                    :aria-label="`Status for ${row.employee}`"
                    @change="onPayrollStatusChange(row, $event)"
                  />
                </td>
              </tr>
              <tr v-if="!isLoadingUsers && payrollRows.length === 0">
                <td colspan="18" class="text-center text-slate-400 italic py-5">No users found for selected filters.</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="font-semibold bg-slate-100 payroll-tfoot-row">
                <td class="payroll-col-no">Total</td>
                <td class="payroll-col-employee payroll-sticky-col payroll-sticky-employee"></td>
                <td class="num">{{ fmt(totals.basicSalary) }}</td>
                <td class="num">{{ displayWorkingDays(totals.workingDays) }}</td>
                <td class="num">{{ fmt(totals.monthlySalary) }}</td>
                <td class="num"></td>
                <td class="num">{{ fmt(totals.totalOt) }}</td>
                <td class="num"></td>
                <td class="num">{{ fmt(totals.totalEarn) }}</td>
                <td class="num"></td>
                <td class="num">{{ fmt(totals.totalConfigDeduct) }}</td>
                <td class="num payroll-highlight">{{ fmt(totals.grossSalary) }}</td>
                <td class="num">{{ fmt(totals.incomeTax) }}</td>
                <td class="num">{{ fmt(totals.pen7) }}</td>
                <td class="num">{{ fmt(totals.pen11) }}</td>
                <td class="num">{{ fmt(totals.otherDeduct) }}</td>
                <td class="num payroll-highlight">{{ fmt(totals.netPay) }}</td>
                <td class="num payroll-col-status"></td>
              </tr>
            </tfoot>
          </table>
          </div>
        </div>

        <div class="flex flex-col items-center py-6 gap-0">
          <button
            type="button"
            class="save-payroll-btn"
            :disabled="!canPreparePayroll || isSavingPayroll"
            @click="savePayroll"
          >
            {{ isSavingPayroll ? 'Saving...' : 'Save' }}
          </button>

          <div class="mt-10 w-full max-w-3xl grid grid-cols-2 sm:grid-cols-4 gap-2 pt-4">
            <span
              :title="preparedByTooltip || undefined"
              class="payroll-prepared-by-label px-2 py-2 text-xs font-semibold text-slate-700 bg-slate-200/80 rounded-md text-center cursor-default select-none"
            >
              {{ preparedByButtonLabel }}
            </span>
            <button
              v-for="action in otherApprovalActions"
              :key="action.key"
              type="button"
              :disabled="!action.canClick || isUpdatingApproval || isSavingPayroll"
              :title="action.tooltip || undefined"
              class="px-2 py-2 text-xs font-semibold text-slate-700 bg-slate-200/80 rounded-md transition-colors text-center disabled:opacity-50 disabled:cursor-not-allowed"
              :class="action.canClick && !isUpdatingApproval && !isSavingPayroll ? 'hover:bg-slate-300' : 'cursor-default'"
              @click="onPayrollApprovalAction(action.key)"
            >
              {{ action.label }}
            </button>
          </div>
        </div>
      </template>

      <p
        v-else
        class="text-sm text-slate-500 text-center py-10 m-0 italic"
      >
        {{ hideDateRange ? 'Select a month to view payroll.' : 'Select a month or date range to view payroll.' }}
      </p>
    </div>

    <Teleport to="body">
      <div
        v-if="flyoutPanel.rowNo !== null"
        ref="flyoutPanelEl"
        class="payroll-flyout fixed z-[9999] bg-white border border-[#cbd5e1] rounded-md shadow-xl text-left"
        :class="{
          'payroll-flyout--list-250': flyoutPanel.mode === 'earn' || flyoutPanel.mode === 'deduct',
          'payroll-flyout--ot-edit': flyoutPanel.mode === 'ot',
          'payroll-flyout--no-truncate': flyoutPanel.mode === 'ot' || flyoutPanel.mode === 'earn'
        }"
        :style="{ top: flyoutPanel.top + 'px', left: flyoutPanel.left + 'px', maxHeight: flyoutPanel.maxH + 'px' }"
        @click.stop
      >
        <div class="px-3 py-2 text-[11px] font-bold text-[#0d4169] border-b border-slate-100 whitespace-nowrap">
          {{ flyoutPanelTitle }}
        </div>
        <div class="overflow-y-auto pb-10 payroll-flyout-body">
          <template v-if="flyoutPanel.mode === 'ot'">
            <div
              v-for="ot in overtimeOptions"
              :key="ot.id"
              class="flex items-center gap-2 px-3 py-1.5 border-b border-slate-50 hover:bg-slate-50"
            >
              <label class="text-xs font-medium text-slate-700 flex-1 truncate" :title="ot.otName">
                {{ ot.otName }}
              </label>
              <input
                type="number"
                step="0.25"
                min="0"
                :value="overtimeHoursForPanel(ot.id)"
                class="ot-hours-input"
                placeholder="Hrs"
                :disabled="!canPreparePayroll || isSavingPayroll"
                :readonly="!canPreparePayroll || isSavingPayroll"
                @input="setOvertimeHoursForPanel(ot.id, $event.target.value)"
              >
              <span class="payroll-flyout-amount text-xs font-semibold text-[#0d4169] min-w-[3.5rem] text-right">
                {{ fmt(overtimePayForOtId(panelRow, ot.id)) }}
              </span>
            </div>
            <p v-if="overtimeOptions.length === 0" class="text-xs text-slate-400 px-3 py-4 text-center m-0">
              No active overtime definitions
            </p>
          </template>
          <template v-else-if="flyoutPanel.mode === 'earn'">
            <div
              v-for="(entry, earnIdx) in panelEarnEntries"
              :key="entry.id || `${entry.definitionId}-${earnIdx}`"
              class="px-3 py-2 border-b border-slate-50 hover:bg-slate-50"
            >
              <div class="payroll-flyout-row payroll-flyout-row--aligned">
                <span class="payroll-flyout-label text-xs font-medium text-slate-700">
                  {{ entry.definitionName || '—' }}
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  class="ot-hours-input"
                  :value="entry.amount"
                  :disabled="!canPreparePayroll || isSavingPayroll"
                  :readonly="!canPreparePayroll || isSavingPayroll"
                  :aria-label="`Earn amount for ${entry.definitionName || 'entry'}`"
                  @input="setConfigEntryAmount(panelRow, 'earn', earnIdx, $event.target.value)"
                >
                <span class="payroll-flyout-amount text-xs font-semibold text-[#0d4169]">
                  {{ fmt(configEntryPayAmount(panelRow, entry)) }}
                </span>
              </div>
              <div class="text-[10px] text-slate-400 mt-1">
                {{ formatConfigAmountTypeLabel(entry) }}
              </div>
            </div>
            <p v-if="panelEarnEntries.length === 0" class="text-xs text-slate-400 px-3 py-4 text-center m-0">
              No earn configuration for this employee.
            </p>
          </template>
          <template v-else-if="flyoutPanel.mode === 'deduct'">
            <div
              v-for="(entry, deductIdx) in panelDeductEntries"
              :key="entry.id || `${entry.definitionId}-${deductIdx}`"
              class="px-3 py-2 border-b border-slate-50 hover:bg-slate-50"
            >
              <div class="payroll-flyout-row payroll-flyout-row--aligned">
                <span class="payroll-flyout-label text-xs font-medium text-slate-700" :title="entry.definitionName">
                  {{ entry.definitionName || '—' }}
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  class="ot-hours-input"
                  :value="entry.amount"
                  :disabled="!canPreparePayroll || isSavingPayroll"
                  :readonly="!canPreparePayroll || isSavingPayroll"
                  :aria-label="`Deduct amount for ${entry.definitionName || 'entry'}`"
                  @input="setConfigEntryAmount(panelRow, 'deduct', deductIdx, $event.target.value)"
                >
                <span class="payroll-flyout-amount text-xs font-semibold text-[#0d4169]">
                  {{ fmt(configEntryPayAmount(panelRow, entry)) }}
                </span>
              </div>
              <div class="text-[10px] text-slate-400 mt-1">
                {{ formatConfigAmountTypeLabel(entry) }}
              </div>
            </div>
            <p v-if="panelDeductEntries.length === 0" class="text-xs text-slate-400 px-3 py-4 text-center m-0">
              No deduct configuration for this employee.
            </p>
          </template>
        </div>
        <button
          type="button"
          class="absolute bottom-2 right-2 text-[11px] font-bold text-[#2563eb] underline"
          @click="closeFlyout"
        >
          Close
        </button>
      </div>
    </Teleport>
  </PayrollShell>
</template>

<script setup>
import { computed, defineProps, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import PayrollShell from './payrollShell.vue'
import FinanceToast from '../FinanceToast.vue'
import MonthlyConfigurationFilters from '../monthlyConfiguration/monthlyConfigurationFilters.vue'
import { resetMonthlyConfigurationForm, useMonthlyConfigurationFilters } from '../monthlyConfiguration/useMonthlyConfigurationFilters.js'
import { apiFetch } from '../../../utils/api'
import { ALL_BRANCHES_SCOPE_VALUE } from '../monthlyConfiguration/useMonthlyConfigurationBranches.js'
import { MAIN_COMPANY_SCOPE_VALUE } from '../monthlyConfiguration/monthlyConfigurationScope.js'
import {
  ETHIOPIAN_MONTH_DAYS,
  getGregorianRangeForSelectedMonth,
  getMonthOptionByValue
} from '../../../utils/ethiopianMonth'

const props = defineProps({
  apiBase: {
    type: String,
    default: '/finance'
  },
  skipPrivilegeCheck: {
    type: Boolean,
    default: false
  },
  payrollPrivilegeName: {
    type: String,
    default: 'Payroll List'
  },
  changePageName: {
    type: String,
    default: 'Payroll'
  },
  hideDateRange: {
    type: Boolean,
    default: false
  }
})

const store = useStore()
const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const payrollApiPath = computed(() => `${props.apiBase}/payroll`)
const incomeTaxApiPath = computed(() => `${props.apiBase}/income-tax`)
const pensionApiPath = computed(() => `${props.apiBase}/pension`)
const earnAndDeductConfigApiPath = computed(() => `${props.apiBase}/earn-and-deduct-config`)
const overtimeApiPath = computed(() => `${props.apiBase}/overtime`)
const monthlyConfigurationApiPath = computed(() => `${props.apiBase}/monthly-configuration`)
const payrollStatusDraftPrefix = computed(() =>
  String(props.apiBase || '/finance').startsWith('/hr')
    ? 'hr_payroll_status_draft_v1'
    : 'finance_payroll_status_draft_v1'
)

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const PAYROLL_FIELD_EDIT_ACTIONS = [
  'Edit',
  'Prepared By',
  'Checked By',
  'Cross Checked By',
  'Approved By'
]

const canPreparePayroll = computed(() => {
  if (props.skipPrivilegeCheck || hasAllPrivilege.value) return true
  const entry = getPrivilegeEntry(props.payrollPrivilegeName)
  return PAYROLL_FIELD_EDIT_ACTIONS.some((action) => getActionFlag(entry, action))
})

function canPerformApprovalAction(action) {
  if (props.skipPrivilegeCheck || hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(props.payrollPrivilegeName), action)
}

function firstNameFromFullName(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean)
  return parts[0] || ''
}

function uniqueApproversByField(idField, nameField) {
  const seen = new Set()
  const list = []
  for (const row of savedPayrollRecords.value) {
    const id = row?.[idField]
    if (!id || seen.has(id)) continue
    seen.add(id)
    list.push({
      id,
      fullName: String(row?.[nameField] || '').trim() || `User #${id}`
    })
  }
  return list
}

function buildApprovalButtonLabel(baseLabel, approvers) {
  if (!approvers.length) return baseLabel
  if (approvers.length === 1) {
    const name = firstNameFromFullName(approvers[0].fullName)
    return name ? `${baseLabel} ${name}` : baseLabel
  }
  const first = firstNameFromFullName(approvers[0].fullName)
  return first ? `${baseLabel} ${first} and others` : `${baseLabel} and others`
}

function buildApprovalTooltip(approvers) {
  if (approvers.length <= 1) return ''
  return approvers.map((p) => p.fullName).join(', ')
}

const savedPayrollRecords = ref([])
const isUpdatingApproval = ref(false)

const uniquePreparers = computed(() => uniqueApproversByField('preparedBy', 'preparedByFullName'))

const preparedByButtonLabel = computed(() => buildApprovalButtonLabel('Prepared By', uniquePreparers.value))

const preparedByTooltip = computed(() => buildApprovalTooltip(uniquePreparers.value))

const otherApprovalActions = computed(() => {
  const checkedApprovers = uniqueApproversByField('checkedBy', 'checkedByFullName')
  const crossCheckedApprovers = uniqueApproversByField('crossCheckedBy', 'crossCheckedByFullName')
  const approvedApprovers = uniqueApproversByField('approvedBy', 'approvedByFullName')

  return [
    {
      key: 'checked',
      baseLabel: 'Checked By',
      label: buildApprovalButtonLabel('Checked By', checkedApprovers),
      tooltip: buildApprovalTooltip(checkedApprovers),
      canClick:
        canPerformApprovalAction('Checked By') &&
        uniquePreparers.value.length > 0 &&
        savedPayrollRecords.value.length > 0
    },
    {
      key: 'cross_checked',
      baseLabel: 'Cross Checked By',
      label: buildApprovalButtonLabel('Cross Checked By', crossCheckedApprovers),
      tooltip: buildApprovalTooltip(crossCheckedApprovers),
      canClick:
        canPerformApprovalAction('Cross Checked By') &&
        checkedApprovers.length > 0 &&
        savedPayrollRecords.value.length > 0
    },
    {
      key: 'approved',
      baseLabel: 'Approved By',
      label: buildApprovalButtonLabel('Approved By', approvedApprovers),
      tooltip: buildApprovalTooltip(approvedApprovers),
      canClick:
        canPerformApprovalAction('Approved By') &&
        crossCheckedApprovers.length > 0 &&
        savedPayrollRecords.value.length > 0
    }
  ]
})

const { form, isSearchingUsers } = useMonthlyConfigurationFilters()

const toast = reactive({
  show: false,
  message: '',
  variant: 'success'
})
let toastTimer = null

function showToast(message, variant = 'success') {
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.message = message
  toast.variant = variant
  toast.show = true
  const duration = variant === 'error' ? 5000 : 3000
  toastTimer = window.setTimeout(() => {
    toast.show = false
    toast.variant = 'success'
    toastTimer = null
  }, duration)
}

const isSavingPayroll = ref(false)

let payrollStatusDraftTimer = null
let suppressPayrollStatusDraftPersistence = false
let payrollStatusDraftDirty = false

const overtimeNameById = ref(new Map())
const overtimeOptions = ref([])
const incomeTaxBrackets = ref([])
const pensionRates = ref({
  employeeContribution: 0,
  companyContribution: 0
})
const isLoadingUsers = ref(false)
const currentUsers = ref([])
let usersRequestId = 0
const flyoutPanelEl = ref(null)
const flyoutPanel = reactive({
  mode: null,
  rowNo: null,
  top: 0,
  left: 0,
  maxH: 400
})

const MONTH_LABELS = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December'
}

function formatDisplayDate(isoDate) {
  if (!isoDate) return ''
  const d = new Date(`${isoDate}T00:00:00`)
  if (Number.isNaN(d.getTime())) return isoDate
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

function resolvePayrollDueDate(rawDueDate) {
  const due = Number(rawDueDate)
  if (Number.isFinite(due) && due > 0) return due
  return ETHIOPIAN_MONTH_DAYS
}

function englishMonthLabel(monthValue) {
  const option = getMonthOptionByValue(monthValue)
  if (option?.label) return option.label
  const mapped = MONTH_LABELS[Number(monthValue)] || MONTH_LABELS[monthValue]
  if (mapped) return mapped
  // Defensive: never show "September | መስከረም" style labels in the list title.
  return String(monthValue || '')
    .split('|')[0]
    .trim()
}

const payrollPeriodTitle = computed(() => {
  if (form.fromDate && form.toDate) {
    return `Payroll for ${formatDisplayDate(form.fromDate)} - ${formatDisplayDate(form.toDate)}`
  }
  if (form.fromDate || form.toDate) {
    const from = form.fromDate ? formatDisplayDate(form.fromDate) : '—'
    const to = form.toDate ? formatDisplayDate(form.toDate) : '—'
    return `Payroll for ${from} - ${to}`
  }
  if (form.selectedMonth) {
    const range = getGregorianRangeForSelectedMonth(form.selectedMonth)
    const monthLabel = englishMonthLabel(form.selectedMonth)
    if (range?.fromDisplay && range?.toDisplay) {
      return `Payroll for the month of ${monthLabel}  ${range.fromDisplay} to ${range.toDisplay}`
    }
    return `Payroll for the month of ${monthLabel}`
  }
  return 'Payroll for the month of _______'
})

const showPayrollTable = computed(() => {
  if (form.selectedMonth) return true
  if (form.fromDate && form.toDate) return true
  return false
})

const panelRow = computed(() => payrollRows.value.find((r) => r.userId === flyoutPanel.rowNo) || null)
const panelEarnEntries = computed(() => {
  const entries = panelRow.value?.earnEntries
  return Array.isArray(entries) ? entries : []
})
const panelDeductEntries = computed(() => {
  const entries = panelRow.value?.deductEntries
  return Array.isArray(entries) ? entries : []
})
const flyoutPanelTitle = computed(() => {
  if (flyoutPanel.mode === 'ot') return 'Overtime (from attendance)'
  if (flyoutPanel.mode === 'earn') return 'Earn (from configuration)'
  if (flyoutPanel.mode === 'deduct') return 'Deduct (from configuration)'
  return ''
})
const payrollRows = ref([])

const sortedPayrollRows = computed(() => {
  return [...payrollRows.value].sort((a, b) =>
    String(a?.employee || '').localeCompare(String(b?.employee || ''), undefined, { sensitivity: 'base' })
  )
})

function createDefaultPayrollRow(user, idx) {
  return {
    no: idx + 1,
    userId: Number(user.id),
    companyId: user.companyId != null ? Number(user.companyId) : null,
    branchId: user.branchId != null ? Number(user.branchId) : null,
    employee: String(user.fullName || user.username || `U${user.id}`),
    basicSalary: Number(user.salary) || 0,
    dueDate: ETHIOPIAN_MONTH_DAYS,
    workingDays: null,
    monthlySalary: 0,
    overtimeEntries: [],
    totalOt: 0,
    earnEntries: [],
    totalEarn: 0,
    deductEntries: [],
    totalConfigDeduct: 0,
    grossSalary: 0,
    incomeTax: 0,
    pen7: 0,
    pen11: 0,
    otherDeduct: 0,
    netPay: 0,
    status: false
  }
}

function safeJsonParse(raw) {
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function payrollPeriodStorageKeyPart() {
  if (form.selectedMonth) return `m:${String(form.selectedMonth)}`
  if (form.fromDate && form.toDate) return `r:${form.fromDate}_${form.toDate}`
  return 'none'
}

function payrollStatusDraftKey() {
  const user = currentUser.value || {}
  const companyId = user?.companyId ?? user?.company_id ?? user?.company?.id ?? 'na'
  const branchId = String(form.selectedBranchId || 'na')
  const entity = String(form.selectEntity || 'all')
  const departmentId = String(form.departmentId || 'all')
  const searchName = String(form.searchName || '').trim().toLowerCase() || 'none'
  const periodKey = payrollPeriodStorageKeyPart()
  return `${payrollStatusDraftPrefix.value}:${companyId}:${branchId}:${entity}:${departmentId}:${searchName}:${periodKey}`
}

function buildPayrollStatusDraftPayload() {
  const statusByUserId = {}
  for (const row of payrollRows.value) {
    const uid = Number(row.userId)
    if (!Number.isFinite(uid)) continue
    statusByUserId[String(uid)] = row.status === true
  }
  return {
    month: form.selectedMonth || null,
    fromDate: form.fromDate || null,
    toDate: form.toDate || null,
    selectedBranchId: form.selectedBranchId || null,
    selectEntity: form.selectEntity || null,
    departmentId: form.departmentId || null,
    searchName: form.searchName || null,
    statusByUserId
  }
}

function payrollStatusDraftMatchesContext(parsed) {
  if (!parsed || typeof parsed !== 'object') return false
  const monthMatch = String(parsed.month || '') === String(form.selectedMonth || '')
  const rangeMatch =
    String(parsed.fromDate || '') === String(form.fromDate || '') &&
    String(parsed.toDate || '') === String(form.toDate || '')
  if (!(monthMatch || rangeMatch)) return false
  if (String(parsed.selectedBranchId || '') !== String(form.selectedBranchId || '')) return false
  if (String(parsed.selectEntity || '') !== String(form.selectEntity || '')) return false
  if (String(parsed.departmentId || '') !== String(form.departmentId || '')) return false
  if (String(parsed.searchName || '').trim() !== String(form.searchName || '').trim()) return false
  return true
}

function persistPayrollStatusDraftNow() {
  if (suppressPayrollStatusDraftPersistence) return
  if (!payrollStatusDraftDirty) return
  if (!hasValidPeriod()) return
  if (!payrollRows.value.length) return

  const key = payrollStatusDraftKey()
  try {
    localStorage.setItem(key, JSON.stringify(buildPayrollStatusDraftPayload()))
  } catch (err) {
    console.warn('Unable to persist payroll status draft:', err)
  }
}

function schedulePersistPayrollStatusDraft() {
  if (suppressPayrollStatusDraftPersistence) return
  if (payrollStatusDraftTimer) {
    window.clearTimeout(payrollStatusDraftTimer)
    payrollStatusDraftTimer = null
  }
  payrollStatusDraftTimer = window.setTimeout(() => {
    payrollStatusDraftTimer = null
    persistPayrollStatusDraftNow()
  }, 250)
}

function loadPayrollStatusDraftForCurrentContext() {
  const key = payrollStatusDraftKey()
  let raw = null
  try {
    raw = localStorage.getItem(key)
  } catch {
    raw = null
  }
  if (!raw) return null
  const parsed = safeJsonParse(raw)
  if (!payrollStatusDraftMatchesContext(parsed)) return null
  return parsed
}

function clearPayrollStatusDraftForCurrentContext() {
  try {
    localStorage.removeItem(payrollStatusDraftKey())
  } catch {
    // ignore
  }
}

function normalizePayrollStatus(value) {
  return value === true || value === 1 || value === '1' || value === 'true'
}

function onPayrollStatusChange(row, event) {
  if (!row) return
  const checked = event?.target?.checked === true
  const original = payrollRows.value.find((r) => Number(r.userId) === Number(row.userId))
  if (original) {
    original.status = checked
  } else {
    row.status = checked
  }
  payrollStatusDraftDirty = true
  schedulePersistPayrollStatusDraft()
}

function normalizeIncomeTaxBracket(raw) {
  return {
    id: raw?.id,
    from: n(raw?.from),
    upto: n(raw?.upto),
    tax: n(raw?.tax),
    deduction: raw?.deduction != null ? String(raw.deduction) : '',
    enabled: raw?.enabled !== false
  }
}

function parseIncomeTaxDeduction(value) {
  if (value === null || value === undefined || value === '') return 0
  const num = Number(String(value).replace(/,/g, '').trim())
  return Number.isFinite(num) ? num : 0
}

function pickHighestRateIncomeTaxBracket(brackets) {
  if (!brackets.length) return null
  return brackets.reduce((best, row) => {
    const bestRate = n(best?.tax)
    const rowRate = n(row?.tax)
    if (rowRate > bestRate) return row
    if (rowRate < bestRate) return best
    return n(row.upto) >= n(best.upto) ? row : best
  }, brackets[0])
}

function resolveIncomeTaxBracket(grossSalary, brackets) {
  const active = (brackets || []).filter((b) => b && b.enabled !== false)
  if (!active.length) return null
  const gross = n(grossSalary)
  const inRange = active.find((b) => gross >= n(b.from) && gross <= n(b.upto))
  if (inRange) return inRange
  return pickHighestRateIncomeTaxBracket(active)
}

function calculateIncomeTaxForGross(grossSalary, brackets) {
  const bracket = resolveIncomeTaxBracket(grossSalary, brackets)
  if (!bracket) return 0
  const gross = n(grossSalary)
  const rate = n(bracket.tax)
  const deduction = parseIncomeTaxDeduction(bracket.deduction)
  return gross * (rate / 100) + deduction
}

async function loadIncomeTaxBrackets() {
  try {
    const response = await apiFetch(incomeTaxApiPath.value, { method: 'GET' })
    if (!response.ok) {
      incomeTaxBrackets.value = []
      return
    }
    const rows = await response.json().catch(() => [])
    incomeTaxBrackets.value = (Array.isArray(rows) ? rows : [])
      .map(normalizeIncomeTaxBracket)
      .filter((row) => row.enabled)
  } catch {
    incomeTaxBrackets.value = []
  }
}

function parsePensionPercent(value) {
  if (value === null || value === undefined || value === '') return 0
  const num = Number(String(value).replace(/%/g, '').trim())
  return Number.isFinite(num) && num >= 0 ? num : 0
}

function formatPensionPctLabel(value) {
  const pct = parsePensionPercent(value)
  if (pct <= 0) return ''
  return String(pct)
}

const pensionEmployeeHeader = computed(() => {
  const label = formatPensionPctLabel(pensionRates.value.employeeContribution)
  return label ? `Pen. ${label}%` : 'Pen. (Employee)'
})

const pensionCompanyHeader = computed(() => {
  const label = formatPensionPctLabel(pensionRates.value.companyContribution)
  return label ? `Pen. ${label}%` : 'Pen. (Company)'
})

function buildPensionFetchUrl() {
  const scope = String(form.selectedBranchId || '').trim()
  if (
    !scope ||
    scope === String(ALL_BRANCHES_SCOPE_VALUE) ||
    scope === String(MAIN_COMPANY_SCOPE_VALUE)
  ) {
    return pensionApiPath.value
  }
  const branchId = Number(scope)
  if (Number.isFinite(branchId) && branchId > 0) {
    return `${pensionApiPath.value}?branchId=${branchId}`
  }
  return pensionApiPath.value
}

async function loadPensionRates() {
  try {
    const response = await apiFetch(buildPensionFetchUrl(), { method: 'GET' })
    if (!response.ok) {
      pensionRates.value = { employeeContribution: 0, companyContribution: 0 }
      return
    }
    const data = await response.json().catch(() => null)
    if (!data || typeof data !== 'object') {
      pensionRates.value = { employeeContribution: 0, companyContribution: 0 }
      return
    }
    pensionRates.value = {
      employeeContribution: parsePensionPercent(data.employeeContribution),
      companyContribution: parsePensionPercent(data.companyContribution)
    }
  } catch {
    pensionRates.value = { employeeContribution: 0, companyContribution: 0 }
  }
}

function syncRowTotalsFromEntries(row) {
  if (!row) return
  row.totalOt = roundTo2(sumOvertimePayForRow(row))
  row.totalEarn = roundTo2(sumConfigPayForRow(row, 'earn'))
  row.totalConfigDeduct = roundTo2(sumConfigPayForRow(row, 'deduct'))
}

function recalculateRow(row) {
  const days = n(row.workingDays)
  const basic = n(row.basicSalary)
  const due = resolvePayrollDueDate(row.dueDate)
  row.dueDate = due

  // Monthly Salary = (Basic Salary / due_date) * Working Days
  row.monthlySalary = due > 0 ? (basic / due) * days : 0

  // Total OT / Earn / Deduct are editable inputs (may also be synced from flyout entries).
  // Gross Salary = Monthly Salary + Total OT + Total Earn - Total Deduct (config)
  row.grossSalary =
    row.monthlySalary + n(row.totalOt) + n(row.totalEarn) - n(row.totalConfigDeduct)

  const gross = n(row.grossSalary)
  row.incomeTax = calculateIncomeTaxForGross(gross, incomeTaxBrackets.value)
  const employeePct = parsePensionPercent(pensionRates.value.employeeContribution)
  const companyPct = parsePensionPercent(pensionRates.value.companyContribution)
  // Pen. % columns are calculated from Basic Salary
  row.pen7 = (basic * employeePct) / 100
  row.pen11 = (basic * companyPct) / 100
  row.otherDeduct = n(row.incomeTax) + n(row.pen7)
  row.netPay = gross - n(row.otherDeduct)
}

watch(
  payrollRows,
  (newRows) => {
    newRows.forEach((row) => recalculateRow(row))
  },
  { deep: true, immediate: true }
)

function normalizedEntityForFetch() {
  const scope = String(form.selectedBranchId || '').trim()
  if (scope === String(ALL_BRANCHES_SCOPE_VALUE)) return 'all'
  return String(form.selectEntity || '').trim()
}

function buildPayrollPeriodBody() {
  const body = {
    month: form.selectedMonth || null,
    fromDate: form.fromDate || null,
    toDate: form.toDate || null,
    departmentId: normalizedEntityForFetch() === 'by_department' ? form.departmentId || null : null
  }
  return body
}

function buildPayrollSaveRows() {
  return payrollRows.value.map((row) => ({
    userId: row.userId,
    companyId: row.companyId,
    branchId: row.branchId,
    basicSalary: row.basicSalary,
    workingDays: row.workingDays,
    monthlySalary: row.monthlySalary,
    totalOt: row.totalOt,
    overtimeEntries: Array.isArray(row.overtimeEntries) ? row.overtimeEntries : [],
    totalEarn: row.totalEarn,
    earnEntries: Array.isArray(row.earnEntries) ? row.earnEntries : [],
    totalConfigDeduct: row.totalConfigDeduct,
    deductEntries: Array.isArray(row.deductEntries) ? row.deductEntries : [],
    grossSalary: row.grossSalary,
    incomeTax: row.incomeTax,
    pen7: row.pen7,
    pen11: row.pen11,
    otherDeduct: row.otherDeduct,
    netPay: row.netPay,
    status: row.status === true
  }))
}

function loadPayrollForUsers(users) {
  payrollRows.value = users.map((u, idx) => createDefaultPayrollRow(u, idx))
}

function applySavedPayrollSnapshot(savedByUserId) {
  for (const row of payrollRows.value) {
    const saved = savedByUserId.get(Number(row.userId))
    if (!saved) continue

    const savedOvertime = saved.overtimeEntries ?? saved.overtime_entries
    if (savedOvertime != null) {
      // Prefer live attendance OT (already set by applyAttendanceToPayrollRows).
      // Only fall back to the saved payroll snapshot if attendance gave nothing.
      const liveOt = Array.isArray(row.overtimeEntries) ? row.overtimeEntries : []
      if (!liveOt.length) {
        row.overtimeEntries = parseAttendanceOvertimeEntries(savedOvertime)
      }
    }

    const savedEarn = saved.earnEntries ?? saved.earn_entries
    if (savedEarn != null) {
      row.earnEntries = Array.isArray(savedEarn)
        ? savedEarn.map(normalizeConfigEntry)
        : []
    }

    const savedDeduct = saved.deductEntries ?? saved.deduct_entries
    if (savedDeduct != null) {
      row.deductEntries = Array.isArray(savedDeduct)
        ? savedDeduct.map(normalizeConfigEntry)
        : []
    }

    row.basicSalary = n(saved.basicSalary)
    if (saved.workingDays !== null && saved.workingDays !== undefined) {
      row.workingDays = roundTo2(saved.workingDays)
    }
    row.monthlySalary = n(saved.monthlySalary)
    row.totalOt = n(saved.totalOt)
    row.totalEarn = n(saved.totalEarn)
    row.totalConfigDeduct = n(saved.totalConfigDeduct)
    row.grossSalary = n(saved.grossSalary)
    row.incomeTax = n(saved.incomeTax)
    row.pen7 = n(saved.pen7)
    row.pen11 = n(saved.pen11)
    row.otherDeduct = n(saved.otherDeduct)
    row.netPay = n(saved.netPay)
  }
}

function applyPayrollStatusFromDraftOrSaved(savedByUserId) {
  suppressPayrollStatusDraftPersistence = true
  try {
    const draft = loadPayrollStatusDraftForCurrentContext()
    const draftStatusByUserId =
      draft && typeof draft.statusByUserId === 'object' && draft.statusByUserId !== null
        ? draft.statusByUserId
        : null

    payrollStatusDraftDirty = Boolean(draftStatusByUserId)

    for (const row of payrollRows.value) {
      const uid = String(row.userId)
      if (draftStatusByUserId && Object.prototype.hasOwnProperty.call(draftStatusByUserId, uid)) {
        row.status = draftStatusByUserId[uid] === true
        continue
      }
      const saved = savedByUserId.get(Number(row.userId))
      row.status = saved ? normalizePayrollStatus(saved.status) : false
    }
  } finally {
    nextTick(() => {
      suppressPayrollStatusDraftPersistence = false
    })
  }
}

async function fetchPayrollMapForUsers(users) {
  if (!hasValidPeriod() || !users.length) {
    savedPayrollRecords.value = []
    return new Map()
  }

  const params = buildPayrollPeriodQueryParams(
    normalizedEntityForFetch() === 'by_department'
  )
  if (!params) {
    savedPayrollRecords.value = []
    return new Map()
  }

  params.set(
    'userIds',
    users
      .map((u) => Number(u.id))
      .filter((id) => Number.isFinite(id))
      .join(',')
  )
  if (!params.get('userIds')) {
    savedPayrollRecords.value = []
    return new Map()
  }

  try {
    const response = await apiFetch(`${payrollApiPath.value}?${params.toString()}`, { method: 'GET' })
    if (!response.ok) {
      savedPayrollRecords.value = []
      return new Map()
    }
    const rows = await response.json().catch(() => [])
    if (!Array.isArray(rows)) {
      savedPayrollRecords.value = []
      return new Map()
    }
    savedPayrollRecords.value = rows
    const map = new Map()
    for (const row of rows) {
      const uid = Number(row?.userId)
      if (Number.isFinite(uid)) map.set(uid, row)
    }
    return map
  } catch {
    savedPayrollRecords.value = []
    return new Map()
  }
}

async function savePayroll() {
  if (!canPreparePayroll.value) return
  if (!hasValidPeriod()) {
    showToast(
      props.hideDateRange
        ? 'Select a month before saving.'
        : 'Select a month or date range before saving.',
      'error'
    )
    return
  }
  if (!payrollRows.value.length) {
    showToast('No payroll rows to save.', 'error')
    return
  }

  isSavingPayroll.value = true
  try {
    const response = await apiFetch(`${payrollApiPath.value}/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...buildPayrollPeriodBody(),
        changePage: String(props.changePageName || 'Payroll').trim() || 'Payroll',
        rows: buildPayrollSaveRows()
      })
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      showToast(data?.message || 'Failed to save payroll.', 'error')
      return
    }
    showToast(data?.message || 'Payroll saved successfully.', 'success')
    payrollStatusDraftDirty = false
    clearPayrollStatusDraftForCurrentContext()
    await fetchPayrollMapForUsers(currentUsers.value)
    applyPayrollStatusFromDraftOrSaved(
      new Map(
        savedPayrollRecords.value
          .map((row) => [Number(row.userId), row])
          .filter(([uid]) => Number.isFinite(uid))
      )
    )
  } catch {
    showToast('Failed to save payroll.', 'error')
  } finally {
    isSavingPayroll.value = false
  }
}

async function onPayrollApprovalAction(key) {
  const action = otherApprovalActions.value.find((a) => a.key === key)
  if (!action?.canClick || isUpdatingApproval.value) return

  if (!hasValidPeriod()) {
    showToast('Select a month or date range before approval.', 'error')
    return
  }

  if (!savedPayrollRecords.value.length) {
    showToast('No saved payroll records available for approval.', 'error')
    return
  }

  const userIds = savedPayrollRecords.value
    .map((row) => Number(row.userId))
    .filter((id) => Number.isFinite(id) && id > 0)
  if (!userIds.length) {
    showToast('No employees available for approval.', 'error')
    return
  }

  const confirmLabel = action.baseLabel || key
  if (!window.confirm(`Are you sure you want to record "${confirmLabel}"?`)) return

  isUpdatingApproval.value = true
  try {
    const response = await apiFetch(`${payrollApiPath.value}/approval`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...buildPayrollPeriodBody(),
        step: key,
        userIds
      })
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      showToast(data?.message || 'Failed to update approval.', 'error')
      return
    }
    const label = action.baseLabel || key
    showToast(`${label} recorded.`, 'success')
    await fetchPayrollMapForUsers(currentUsers.value)
  } catch {
    showToast('Failed to update approval.', 'error')
  } finally {
    isUpdatingApproval.value = false
  }
}

function buildPayrollPeriodQueryParams(includeDepartment = false) {
  const params = new URLSearchParams()
  if (form.selectedMonth) {
    params.set('month', String(form.selectedMonth))
  } else if (form.fromDate && form.toDate) {
    params.set('fromDate', form.fromDate)
    params.set('toDate', form.toDate)
  } else {
    return null
  }
  if (includeDepartment && normalizedEntityForFetch() === 'by_department') {
    const depId = String(form.departmentId || '').trim()
    if (depId) params.set('departmentId', depId)
  }
  return params
}

function buildAttendanceQueryParams() {
  return buildPayrollPeriodQueryParams(true)
}

function normalizeAttendanceInt(value) {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? Math.trunc(num) : null
}

function normalizeAttendanceDecimal(value) {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

function roundTo2(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return null
  return Math.round((num + Number.EPSILON) * 100) / 100
}

function buildAttendanceMapForUsers(records, users) {
  const byUserId = new Map()
  for (const record of records) {
    const uid = Number(record?.userId)
    if (!Number.isFinite(uid)) continue
    if (!byUserId.has(uid)) byUserId.set(uid, [])
    byUserId.get(uid).push(record)
  }

  const result = new Map()
  for (const user of users) {
    const uid = Number(user.id)
    const candidates = byUserId.get(uid) || []
    if (!candidates.length) continue

    const userBranchId =
      user.branchId === null || user.branchId === undefined ? null : Number(user.branchId)

    const matchesBranch = (r) =>
      (r.branchId === null || r.branchId === undefined ? null : Number(r.branchId)) === userBranchId

    // Priority 1: same branchId + exact departmentId match
    // Priority 2: same branchId + null departmentId (attendance saved without a department)
    // Priority 3: any branchId match (legacy / fallback)
    // Priority 4: first available record
    const pick =
      candidates.find((r) => matchesBranch(r) && r.departmentId != null) ||
      candidates.find((r) => matchesBranch(r) && r.departmentId == null) ||
      candidates.find(matchesBranch) ||
      candidates[0]

    result.set(uid, pick)
  }
  return result
}

function parseAttendanceOvertimeEntries(raw) {
  if (!raw) return []
  const list = Array.isArray(raw) ? raw : []
  return list
    .map((entry) => {
      const otId = normalizeAttendanceInt(entry?.ot_id ?? entry?.otId)
      const hrs = Number(entry?.hrs ?? entry?.hours ?? 0)
      const percentRaw = entry?.percent ?? entry?.otRate ?? entry?.ot_rate
      const percent =
        percentRaw !== null && percentRaw !== undefined && percentRaw !== ''
          ? Number(percentRaw)
          : null
      return {
        ot_id: otId,
        hrs,
        percent: Number.isFinite(percent) ? percent : null
      }
    })
    .filter((entry) => entry.ot_id != null && Number.isFinite(entry.hrs) && entry.hrs > 0)
    .sort((a, b) => a.ot_id - b.ot_id)
}

function applyAttendanceToPayrollRows(attendanceByUserId) {
  for (const row of payrollRows.value) {
    const att = attendanceByUserId.get(Number(row.userId))
    if (!att) {
      row.dueDate = ETHIOPIAN_MONTH_DAYS
      row.workingDays = null
      row.overtimeEntries = []
      continue
    }

    const due = resolvePayrollDueDate(normalizeAttendanceInt(att.dueDate))
    const absent = normalizeAttendanceDecimal(att.absent) ?? 0
    row.dueDate = due
    row.workingDays =
      due != null && due > 0 ? roundTo2(Math.max(0, due - absent)) : null
    row.overtimeEntries = parseAttendanceOvertimeEntries(att.overtime)
  }
}

function normalizeConfigEntry(raw) {
  return {
    id: raw?.id != null ? Number(raw.id) : null,
    definitionId: raw?.definitionId != null ? Number(raw.definitionId) : null,
    definitionName: String(raw?.definitionName || '').trim(),
    amountType: String(raw?.amountType || '').trim(),
    amount: String(raw?.amount ?? '').trim(),
    applicableFor: String(raw?.applicableFor || '').trim()
  }
}

function groupEarnDeductByUser(records) {
  const byUser = new Map()
  for (const raw of records) {
    const uid = Number(raw?.userId)
    if (!Number.isFinite(uid)) continue
    if (!byUser.has(uid)) {
      byUser.set(uid, { earn: [], deduct: [] })
    }
    const entry = normalizeConfigEntry(raw)
    const bucket = String(raw?.beneficiary || '').toLowerCase() === 'deduct' ? 'deduct' : 'earn'
    byUser.get(uid)[bucket].push(entry)
  }
  return byUser
}

function applyEarnDeductToPayrollRows(earnDeductByUserId) {
  for (const row of payrollRows.value) {
    const grouped = earnDeductByUserId.get(Number(row.userId)) || { earn: [], deduct: [] }
    row.earnEntries = grouped.earn
    row.deductEntries = grouped.deduct
  }
}

async function fetchEarnDeductMapForUsers(users) {
  if (!hasValidPeriod() || !users.length) return new Map()

  const params = buildPayrollPeriodQueryParams(false)
  if (!params) return new Map()

  params.set('userIds', users.map((u) => Number(u.id)).filter((id) => Number.isFinite(id)).join(','))
  if (!params.get('userIds')) return new Map()

  try {
    const response = await apiFetch(`${earnAndDeductConfigApiPath.value}/payroll?${params.toString()}`, {
      method: 'GET'
    })
    if (!response.ok) return new Map()
    const rows = await response.json().catch(() => [])
    if (!Array.isArray(rows)) return new Map()
    return groupEarnDeductByUser(rows)
  } catch {
    return new Map()
  }
}

function overtimeIsActive(row) {
  if (!row || typeof row !== 'object') return false
  const active = row.active
  if (active === undefined || active === null) return false
  return active === true || active === 1 || active === '1'
}

async function loadOvertimeNameMap() {
  try {
    const response = await apiFetch(overtimeApiPath.value, { method: 'GET' })
    if (!response.ok) {
      overtimeNameById.value = new Map()
      overtimeOptions.value = []
      return
    }
    const rows = await response.json().catch(() => [])
    const list = Array.isArray(rows) ? rows : []
    const map = new Map()
    const options = []
    for (const row of list) {
      const id = String(row?.id ?? '').trim()
      if (!id) continue
      const name = String(row?.otName || '').trim() || `OT ${id}`
      map.set(id, name)
      if (overtimeIsActive(row)) {
        options.push({
          id,
          otName: name,
          otRate: row.otRate != null && row.otRate !== '' ? Number(row.otRate) : null
        })
      }
    }
    options.sort((a, b) => a.otName.localeCompare(b.otName))
    overtimeNameById.value = map
    overtimeOptions.value = options
  } catch {
    overtimeNameById.value = new Map()
    overtimeOptions.value = []
  }
}

async function fetchAttendanceMapForUsers(users) {
  if (!hasValidPeriod() || !users.length) return new Map()

  const params = buildAttendanceQueryParams()
  if (!params) return new Map()

  try {
    const response = await apiFetch(`${monthlyConfigurationApiPath.value}/attendance?${params.toString()}`, {
      method: 'GET'
    })
    if (!response.ok) return new Map()
    const rows = await response.json().catch(() => [])
    if (!Array.isArray(rows)) return new Map()
    return buildAttendanceMapForUsers(rows, users)
  } catch {
    return new Map()
  }
}

function hasValidPeriod() {
  return Boolean(form.selectedMonth || (form.fromDate && form.toDate))
}

function canFetchPayrollUsers() {
  if (!hasValidPeriod()) return false
  const scope = String(form.selectedBranchId || '').trim()
  if (!scope) return false
  if (scope === String(ALL_BRANCHES_SCOPE_VALUE)) return true

  const entity = normalizedEntityForFetch()
  if (!entity) return false
  if (entity === 'by_department') return Boolean(String(form.departmentId || '').trim())
  if (entity === 'by_name') return Boolean(String(form.searchName || '').trim()) && !isSearchingUsers.value
  return entity === 'all'
}

async function loadPayrollUsers() {
  if (!canFetchPayrollUsers()) {
    currentUsers.value = []
    payrollRows.value = []
    savedPayrollRecords.value = []
    payrollStatusDraftDirty = false
    closeFlyout()
    return
  }
  const requestId = ++usersRequestId
  isLoadingUsers.value = true
  try {
    const params = new URLSearchParams()
    params.set('scope', String(form.selectedBranchId || '').trim())
    params.set('entity', normalizedEntityForFetch() || 'all')
    if (form.departmentId) params.set('departmentId', String(form.departmentId))
    if (String(form.searchName || '').trim()) params.set('q', String(form.searchName || '').trim())
    const response = await apiFetch(`/users/monthly-configuration/payroll-users?${params.toString()}`, {
      method: 'GET'
    })
    if (requestId !== usersRequestId) return
    if (!response.ok) {
      currentUsers.value = []
      payrollRows.value = []
      return
    }
    const users = await response.json().catch(() => [])
    currentUsers.value = Array.isArray(users) ? users : []
    loadPayrollForUsers(currentUsers.value)
    const [attendanceByUserId, earnDeductByUserId] = await Promise.all([
      fetchAttendanceMapForUsers(currentUsers.value),
      fetchEarnDeductMapForUsers(currentUsers.value),
      loadOvertimeNameMap()
    ])
    if (requestId !== usersRequestId) return
    applyAttendanceToPayrollRows(attendanceByUserId)
    applyEarnDeductToPayrollRows(earnDeductByUserId)
    for (const row of payrollRows.value) {
      syncRowTotalsFromEntries(row)
    }
    const payrollByUserId = await fetchPayrollMapForUsers(currentUsers.value)
    if (requestId !== usersRequestId) return
    applySavedPayrollSnapshot(payrollByUserId)
    applyPayrollStatusFromDraftOrSaved(payrollByUserId)
  } catch {
    if (requestId !== usersRequestId) return
    currentUsers.value = []
    payrollRows.value = []
  } finally {
    if (requestId === usersRequestId) {
      isLoadingUsers.value = false
    }
  }
}

function n(v) {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

function fmt(value) {
  if (value === null || value === undefined || value === '') return ''
  const num = Number(value)
  if (Number.isNaN(num)) return ''
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function displayWorkingDays(value) {
  if (value === null || value === undefined || value === '') return ''
  const num = Number(value)
  if (!Number.isFinite(num)) return ''
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function onWorkingDaysChange(row) {
  if (!row) return
  const raw = row.workingDays
  if (raw === null || raw === undefined || raw === '') {
    row.workingDays = null
  } else {
    const num = Number(raw)
    row.workingDays = Number.isFinite(num) ? roundTo2(Math.max(0, num)) : null
  }
  // Monthly salary drives salary-% earn/deduct; keep Total OT as currently set.
  const days = n(row.workingDays)
  const basic = n(row.basicSalary)
  const due = resolvePayrollDueDate(row.dueDate)
  row.dueDate = due
  row.monthlySalary = due > 0 ? (basic / due) * days : 0
  row.totalEarn = roundTo2(sumConfigPayForRow(row, 'earn'))
  row.totalConfigDeduct = roundTo2(sumConfigPayForRow(row, 'deduct'))
  recalculateRow(row)
}

function normalizeEditableAmount(raw) {
  if (raw === null || raw === undefined || raw === '') return 0
  const num = Number(raw)
  if (!Number.isFinite(num)) return 0
  return roundTo2(Math.max(0, num))
}

function onTotalOtChange(row) {
  if (!row) return
  row.totalOt = normalizeEditableAmount(row.totalOt)
  recalculateRow(row)
}

function onTotalEarnChange(row) {
  if (!row) return
  row.totalEarn = normalizeEditableAmount(row.totalEarn)
  recalculateRow(row)
}

function onTotalConfigDeductChange(row) {
  if (!row) return
  row.totalConfigDeduct = normalizeEditableAmount(row.totalConfigDeduct)
  recalculateRow(row)
}

function normalizeHoursValue(raw) {
  if (raw === null || raw === undefined || raw === '') return 0
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.round(n * 100) / 100
}

function overtimePercentForOtId(otId) {
  const id = String(otId)
  const opt = overtimeOptions.value.find((row) => String(row.id) === id)
  if (!opt || opt.otRate == null || opt.otRate === '') return null
  const percent = Number(opt.otRate)
  return Number.isFinite(percent) ? percent : null
}

function overtimeHoursForPanel(otId) {
  const row = panelRow.value
  if (!row) return ''
  const entries = Array.isArray(row.overtimeEntries) ? row.overtimeEntries : []
  const found = entries.find((e) => String(e?.ot_id) === String(otId))
  const hrs = Number(found?.hrs || 0)
  return hrs > 0 ? String(hrs) : ''
}

function setOvertimeHoursForPanel(otId, raw) {
  const row = panelRow.value
  if (!row || !canPreparePayroll.value) return
  const key = String(otId)
  const hrs = normalizeHoursValue(raw)
  const percent = overtimePercentForOtId(otId)
  let entries = Array.isArray(row.overtimeEntries) ? [...row.overtimeEntries] : []
  const idx = entries.findIndex((e) => String(e?.ot_id) === key)
  if (hrs <= 0 || raw === '' || raw == null) {
    if (idx >= 0) entries.splice(idx, 1)
  } else {
    const next = {
      ot_id: Number(otId),
      hrs,
      percent: percent != null ? percent : idx >= 0 ? entries[idx].percent ?? null : null
    }
    if (idx >= 0) entries[idx] = next
    else entries.push(next)
  }
  entries = entries
    .filter((e) => e.ot_id != null && Number.isFinite(Number(e.hrs)) && Number(e.hrs) > 0)
    .sort((a, b) => Number(a.ot_id) - Number(b.ot_id))
  row.overtimeEntries = entries
  row.totalOt = roundTo2(sumOvertimePayForRow(row))
  recalculateRow(row)
}

function overtimePayForOtId(row, otId) {
  const entries = Array.isArray(row?.overtimeEntries) ? row.overtimeEntries : []
  const found = entries.find((e) => String(e?.ot_id) === String(otId))
  if (!found) return 0
  return overtimePayForEntry(row, found)
}

function setConfigEntryAmount(row, kind, index, raw) {
  if (!row || !canPreparePayroll.value) return
  const key = kind === 'deduct' ? 'deductEntries' : 'earnEntries'
  const entries = Array.isArray(row[key]) ? row[key] : []
  const entry = entries[index]
  if (!entry) return
  const trimmed = String(raw ?? '').trim()
  if (trimmed === '') {
    entry.amount = ''
  } else {
    const num = Number(trimmed)
    entry.amount = Number.isFinite(num) ? String(num) : trimmed
  }
  if (kind === 'deduct') {
    row.totalConfigDeduct = roundTo2(sumConfigPayForRow(row, 'deduct'))
  } else {
    row.totalEarn = roundTo2(sumConfigPayForRow(row, 'earn'))
  }
  recalculateRow(row)
}

function formatConfigAmountTypeLabel(entry) {
  const type = String(entry?.amountType || '').toLowerCase()
  if (type === 'salary_percent') return 'salary %'
  if (type === 'fixed') return 'fixed'
  return type || 'amount'
}

function overtimeRateForEntry(row, entry) {
  const basic = n(row?.basicSalary)
  const due = resolvePayrollDueDate(row?.dueDate)
  const percent = entry?.percent != null && entry?.percent !== '' ? Number(entry.percent) : 0
  const pct = Number.isFinite(percent) ? percent : 0
  if (basic <= 0 || due <= 0) return 0
  return (basic / (due * 8)) * (1 + pct / 100)
}

function overtimePayForEntry(row, entry) {
  const hrs = n(entry?.hrs)
  if (hrs <= 0) return 0
  return hrs * overtimeRateForEntry(row, entry)
}

function sumOvertimePayForRow(row) {
  const entries = Array.isArray(row?.overtimeEntries) ? row.overtimeEntries : []
  return entries.reduce((sum, entry) => sum + overtimePayForEntry(row, entry), 0)
}

function flyoutButtonLabel(entries) {
  const count = Array.isArray(entries) ? entries.length : 0
  return count > 0 ? 'Edit' : 'List'
}

function configEntryPayAmount(row, entry) {
  const amount = Number(entry?.amount)
  if (!Number.isFinite(amount)) return 0
  const type = String(entry?.amountType || '').toLowerCase()
  if (type === 'salary_percent') {
    return (n(row?.monthlySalary) * amount) / 100
  }
  if (type === 'fixed') {
    return amount
  }
  return 0
}

function sumConfigPayForRow(row, kind) {
  const key = kind === 'deduct' ? 'deductEntries' : 'earnEntries'
  const entries = Array.isArray(row?.[key]) ? row[key] : []
  return entries.reduce((sum, entry) => sum + configEntryPayAmount(row, entry), 0)
}

function positionFlyoutWithinViewport(anchorRect) {
  const el = flyoutPanelEl.value
  if (!el) return

  const pad = 8
  const scrollY = window.scrollY || window.pageYOffset
  const scrollX = window.scrollX || window.pageXOffset
  const panelW = el.offsetWidth
  const viewportLeft = scrollX + pad
  const viewportRight = scrollX + window.innerWidth - pad

  let left = anchorRect.left + scrollX
  if (left + panelW > viewportRight) {
    left = Math.max(viewportLeft, viewportRight - panelW)
  }
  if (left < viewportLeft) {
    left = viewportLeft
  }
  flyoutPanel.left = left

  const panelH = el.offsetHeight
  const topBelow = anchorRect.bottom + scrollY + 6
  const maxTop = scrollY + window.innerHeight - panelH - pad
  flyoutPanel.top = Math.max(scrollY + pad, Math.min(topBelow, maxTop))
}

function openFlyout(evt, rowNo, mode) {
  const rect = evt.currentTarget.getBoundingClientRect()
  const scrollY = window.scrollY || window.pageYOffset
  const scrollX = window.scrollX || window.pageXOffset
  flyoutPanel.mode = mode
  flyoutPanel.rowNo = rowNo
  const topBelow = rect.bottom + scrollY + 6
  const leftAnchored = rect.left + scrollX
  const maxH = window.innerHeight - (rect.bottom - window.scrollY + 80)
  flyoutPanel.maxH = Math.max(240, Math.min(440, maxH))
  flyoutPanel.top = Math.max(scrollY + 8, Math.min(topBelow, scrollY + window.innerHeight - 120))
  flyoutPanel.left = leftAnchored
  nextTick(() => {
    positionFlyoutWithinViewport(rect)
    bindOutsideClose()
  })
}

function closeFlyout() {
  flyoutPanel.mode = null
  flyoutPanel.rowNo = null
  removeOutsideClose()
}

let outsideHandler = null

function bindOutsideClose() {
  removeOutsideClose()
  outsideHandler = (e) => {
    const el = flyoutPanelEl.value
    if (el && el.contains(e.target)) return
    closeFlyout()
  }
  document.addEventListener('pointerdown', outsideHandler)
}

function removeOutsideClose() {
  if (outsideHandler) {
    document.removeEventListener('pointerdown', outsideHandler)
    outsideHandler = null
  }
}

function sumField(field) {
  return payrollRows.value.reduce((acc, row) => {
    const v = row[field]
    if (v === null || v === undefined || v === '') return acc
    const num = Number(v)
    return Number.isNaN(num) ? acc : acc + num
  }, 0)
}

const totals = computed(() => ({
  basicSalary: sumField('basicSalary'),
  workingDays: sumField('workingDays'),
  monthlySalary: sumField('monthlySalary'),
  totalOt: sumField('totalOt'),
  totalEarn: sumField('totalEarn'),
  totalConfigDeduct: sumField('totalConfigDeduct'),
  grossSalary: sumField('grossSalary'),
  incomeTax: sumField('incomeTax'),
  pen7: sumField('pen7'),
  pen11: sumField('pen11'),
  otherDeduct: sumField('otherDeduct'),
  netPay: sumField('netPay')
}))

watch(
  incomeTaxBrackets,
  () => {
    payrollRows.value.forEach((row) => recalculateRow(row))
  },
  { deep: true }
)

watch(
  pensionRates,
  () => {
    payrollRows.value.forEach((row) => recalculateRow(row))
  },
  { deep: true }
)

watch(
  () => form.selectedBranchId,
  () => {
    loadPensionRates()
  }
)

onMounted(async () => {
  // Reset shared monthly-configuration filters when entering payroll.
  resetMonthlyConfigurationForm()
  await Promise.all([loadIncomeTaxBrackets(), loadPensionRates()])
  loadPayrollUsers()
})

watch(
  () => [
    form.selectedMonth,
    form.fromDate,
    form.toDate,
    form.selectedBranchId,
    form.selectEntity,
    form.departmentId,
    form.searchName,
    isSearchingUsers.value
  ],
  () => {
    loadPayrollUsers()
  }
)

onBeforeUnmount(() => {
  if (payrollStatusDraftTimer) {
    window.clearTimeout(payrollStatusDraftTimer)
    payrollStatusDraftTimer = null
  }
  removeOutsideClose()
})
</script>

<style scoped>
.payroll-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: separate;
  border-spacing: 0;
}

.payroll-table th,
.payroll-table td {
  padding: 4px 10px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  font-size: 0.72rem;
  line-height: 1.2;
  white-space: nowrap;
}

.payroll-table thead th {
  background: #f1f5f9;
  font-weight: 600;
  color: #1e293b;
  text-align: center;
  position: sticky;
  top: 0;
  z-index: 20;
}

.payroll-col-status {
  width: 4.5rem;
  min-width: 4.5rem;
  text-align: center;
}

.payroll-status-checkbox {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
  accent-color: #0d4169;
}

.payroll-status-checkbox:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.payroll-col-no {
  width: 2.75rem;
  min-width: 2.75rem;
  max-width: 2.75rem;
}

.payroll-col-employee {
  width: max-content;
  min-width: 6rem;
  max-width: none;
  white-space: nowrap;
}

.payroll-sticky-col {
  position: sticky;
  background: #ffffff;
  z-index: 10;
}

.payroll-sticky-employee {
  left: 0;
}

.payroll-table thead .payroll-sticky-col {
  z-index: 30;
  background: #f1f5f9;
}

.payroll-table tfoot .payroll-sticky-col {
  background: #f1f5f9;
  z-index: 10;
}

.payroll-table tbody tr:hover .payroll-sticky-col {
  background: #f8fafc;
}

.payroll-table tbody td.payroll-col-no,
.payroll-table thead th.payroll-col-no,
.payroll-table tfoot td.payroll-col-no {
  text-align: center;
}

.payroll-table tbody td {
  text-align: left;
}

.payroll-table tbody tr:hover {
  background: #f8fafc;
}

.payroll-table tfoot td {
  background: #f1f5f9;
  border-top: 2px solid #cbd5e1;
  font-weight: 600;
}

.payroll-table tbody td.payroll-col-employee,
.payroll-table thead th.payroll-col-employee,
.payroll-table tfoot td.payroll-col-employee {
  text-align: left;
}

.payroll-table th.num,
.payroll-table td.num {
  width: 5.5rem;
  min-width: 5.5rem;
  text-align: center;
}

.payroll-table th.col-overt,
.payroll-table td.col-overt,
.payroll-table th.col-flyout,
.payroll-table td.col-flyout {
  width: 6.5rem;
  min-width: 6.5rem;
}

.payroll-flyout {
  width: max-content;
  max-width: calc(100vw - 1rem);
  min-width: 0;
}

.payroll-flyout--list-250 {
  width: 280px;
  min-width: 280px;
  max-width: min(280px, calc(100vw - 1rem));
  box-sizing: border-box;
}

.payroll-flyout--ot-edit {
  width: 22rem;
  min-width: 18rem;
  max-width: min(22rem, calc(100vw - 1rem));
  box-sizing: border-box;
}

.payroll-flyout--list-250:not(.payroll-flyout--no-truncate) .payroll-flyout-body {
  overflow-x: hidden;
}

.payroll-flyout--list-250.payroll-flyout--no-truncate .payroll-flyout-body {
  overflow-x: visible;
}

.payroll-flyout--list-250 > div:first-child {
  white-space: normal;
}

.payroll-flyout-body {
  max-height: 16rem;
  overflow-y: auto;
  overflow-x: auto;
  max-width: 100%;
}

.payroll-flyout-row {
  width: max-content;
  max-width: none;
}

.payroll-flyout-row--aligned {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  column-gap: 0.5rem;
  width: 100%;
  box-sizing: border-box;
}

.payroll-flyout--no-truncate .payroll-flyout-row--aligned {
  align-items: start;
}

.payroll-flyout--list-250:not(.payroll-flyout--no-truncate) .payroll-flyout-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.payroll-flyout--no-truncate .payroll-flyout-label {
  min-width: 0;
  overflow: visible;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.payroll-flyout-metric,
.payroll-flyout-amount {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.payroll-flyout--no-truncate .payroll-flyout-metric {
  white-space: normal;
  overflow-wrap: anywhere;
}

.payroll-table input.income-table-field {
  box-sizing: border-box;
  width: 100%;
  padding: 2px 4px;
  border: 1px solid transparent;
  border-radius: 3px;
  outline: none;
  background-color: #fef2f2;
  font-size: 0.72rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
  transition: all 0.15s;
}

.payroll-table input.income-table-field:focus {
  border-color: #0d4169;
  background-color: #fff;
}

.payroll-table input.income-table-field:disabled,
.payroll-table input.income-table-field:read-only {
  opacity: 0.65;
  cursor: not-allowed;
  background-color: #f1f5f9;
}

.flyout-action-btn {
  width: 100%;
  padding: 2px 6px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 0.68rem;
  font-weight: 600;
  color: #0d4169;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
}

.flyout-action-btn:hover {
  border-color: #0d4169;
}

.ot-hours-input {
  width: 3.9rem;
  padding: 2px 4px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  outline: none;
  text-align: center;
  font-size: 0.72rem;
  font-weight: 600;
  color: #0d4169;
  background: #fef2f2;
}

.ot-hours-input:focus {
  border-color: #0d4169;
  background: #fff;
}

.ot-hours-input:disabled,
.ot-hours-input:read-only {
  opacity: 0.65;
  cursor: not-allowed;
  background: #f1f5f9;
}

.payroll-table td.payroll-highlight {
  font-weight: 700;
  color: #475569;
}

.save-payroll-btn {
  min-width: 120px;
  padding: 6px 32px;
  background-color: #0d4169;
  color: white;
  font-size: 0.875rem;
  font-weight: 700;
  border: none;
  border-radius: 6px;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.save-payroll-btn:hover:not(:disabled) {
  background-color: #0a3354;
}

.save-payroll-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.save-payroll-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
